<template>
  <div class="contract-type-spec-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('contractTypeSpecManage.table.title')" :subtitle="$t('contractTypeSpecManage.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="handleGoBack">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.specCd" clearable
            :placeholder="$t('contractTypeSpecManage.search.specCd')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.specName" clearable
            :placeholder="$t('contractTypeSpecManage.search.specName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.specShow" clearable class="w-full"
            :placeholder="$t('contractTypeSpecManage.search.specShow')">
            <el-option :label="$t('contractTypeSpecManage.search.all')" value="" />
            <el-option :label="$t('contractTypeSpecManage.search.yes')" value="Y" />
            <el-option :label="$t('contractTypeSpecManage.search.no')" value="N" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('contractTypeSpecManage.table.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('contractTypeSpecManage.page.empty')" @retry="getList">
        <el-table-column prop="specCd" :label="$t('contractTypeSpecManage.table.specCd')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.specCd || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="specName" :label="$t('contractTypeSpecManage.table.specName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="required" :label="$t('contractTypeSpecManage.table.required')" align="center" width="90">
          <template slot-scope="scope">
            {{ scope.row.required === 'Y' ? $t('common.yes') : $t('common.no') }}
          </template>
        </el-table-column>
        <el-table-column prop="specShow" :label="$t('contractTypeSpecManage.table.specShow')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.specShow === 'Y' ? $t('common.yes') : $t('common.no') }}
          </template>
        </el-table-column>
        <el-table-column prop="specValueType" :label="$t('contractTypeSpecManage.table.specValueType')" align="center" width="110">
          <template slot-scope="scope">
            {{ formatSpecValueType(scope.row.specValueType) }}
          </template>
        </el-table-column>
        <el-table-column prop="specType" :label="$t('contractTypeSpecManage.table.specType')" align="center" width="110">
          <template slot-scope="scope">
            {{ formatSpecType(scope.row.specType) }}
          </template>
        </el-table-column>
        <el-table-column prop="listShow" :label="$t('contractTypeSpecManage.table.listShow')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.listShow === 'Y' ? $t('common.yes') : $t('common.no') }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <add-contract-type-spec ref="addDialog" @success="handleSuccess" />
    <edit-contract-type-spec ref="editDialog" @success="handleSuccess" />
    <delete-contract-type-spec ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import { queryContractTypeSpec } from '@/api/contract/contractTypeSpecManageApi'
import AddContractTypeSpec from '@/components/contract/addContractTypeSpec'
import EditContractTypeSpec from '@/components/contract/editContractTypeSpec'
import DeleteContractTypeSpec from '@/components/contract/deleteContractTypeSpec'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ContractTypeSpecManageList',
  components: {
    AddContractTypeSpec,
    EditContractTypeSpec,
    DeleteContractTypeSpec,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        specCd: '',
        specName: '',
        specShow: '',
        contractTypeId: this.$route.query.contractTypeId || ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      typeName: this.$route.query.typeName || ''
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const { data, total } = await queryContractTypeSpec(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取合同扩展属性失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('contractTypeSpecManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        specCd: '',
        specName: '',
        specShow: '',
        contractTypeId: this.$route.query.contractTypeId || ''
      }
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleAdd() {
      this.$refs.addDialog.open({
        contractTypeId: this.searchForm.contractTypeId
      })
    },
    handleEdit(row) {
      this.$refs.editDialog.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleGoBack() {
      this.$router.go(-1)
    },
    handleSuccess() {
      this.getList()
    },
    formatSpecValueType(type) {
      const map = {
        '1001': this.$t('contractTypeSpecManage.specValueType.string'),
        '2002': this.$t('contractTypeSpecManage.specValueType.integer'),
        '3003': this.$t('contractTypeSpecManage.specValueType.amount'),
        '4004': this.$t('contractTypeSpecManage.specValueType.date'),
        '5005': this.$t('contractTypeSpecManage.specValueType.time')
      }
      return map[type] || type
    },
    formatSpecType(type) {
      const map = {
        '2233': this.$t('contractTypeSpecManage.specType.input'),
        '3344': this.$t('contractTypeSpecManage.specType.select')
      }
      return map[type] || type
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-type-spec-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
